import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BillingInterval } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { useMe, usePricing } from '../lib/queries';
import { ErrorNotice, Spinner } from '../components/ui';
import { usePageMeta } from '../lib/pageMeta';

const INTERVAL_LABEL: Record<BillingInterval, string> = {
  month: 'Monthly',
  quarter: '3-month bundle',
};

const INTERVAL_SUFFIX: Record<BillingInterval, string> = {
  month: 'USD / month',
  quarter: 'USD for 3 months',
};

export function Pricing() {
  usePageMeta({
    title: 'Pricing',
    description:
      "Titrate's plans for CAOP and OPRA practice: a free diagnostic for each exam, then full-length simulations, AI feedback and revision timetables on a paid plan.",
    path: '/pricing',
  });
  const { data, isLoading } = usePricing();
  const { data: me } = useMe();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /*
   * Defaults to the bundle where it is sold. Preparation runs to a fixed exam
   * date — the planner assumes eight weeks — so the quarter is the interval
   * that actually matches how the product gets used, and leading with it is
   * more honest than defaulting to the one that looks cheapest per row.
   */
  const [interval, setInterval] = useState<BillingInterval>('quarter');

  const intervals = data?.intervals ?? ['month'];
  const active: BillingInterval = intervals.includes(interval) ? interval : 'month';

  const checkout = async (tier: 'standard' | 'advanced') => {
    setError(null);
    setBusy(tier);
    try {
      const { url } = await api.post<{ url: string }>('/api/billing/checkout', {
        tier,
        interval: active,
      });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not start checkout.');
      setBusy(null);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-8">
      <header className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">Simple pricing</h1>
        <p className="mx-auto mt-3 max-w-xl text-muted">
          Start with a free diagnostic for each exam — your weighted score, a domain-by-domain
          breakdown and an honest read on your pace. A paid plan is what you move to when you want
          to rehearse the full paper under time and be told what to do about the result.
        </p>
      </header>

      {intervals.length > 1 ? (
        <div className="flex justify-center">
          <div
            className="inline-flex rounded-lg border border-line bg-surface p-1"
            role="radiogroup"
            aria-label="Billing period"
          >
            {intervals.map((option) => {
              const selected = option === active;
              // Every paid plan is discounted by the same proportion, so one
              // badge on the control says it rather than one per card.
              const saving = data?.plans.find((p) => p.tier === 'standard')?.savingPct[option] ?? 0;
              return (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setInterval(option)}
                  className={`min-h-11 rounded px-4 text-sm font-semibold transition sm:min-h-0 sm:py-2 ${
                    selected ? 'bg-brand-700 text-white' : 'text-muted hover:bg-raised'
                  }`}
                >
                  {INTERVAL_LABEL[option]}
                  {saving > 0 ? (
                    <span className={`ml-2 text-xs font-medium ${selected ? 'text-brand-100' : 'text-brand-700 dark:text-brand-300'}`}>
                      save {saving}%
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {error ? <ErrorNotice message={error} /> : null}
      {data && !data.stripeConfigured ? (
        <div className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-200">
          Billing is not configured on this deployment yet, so the upgrade buttons are inactive.
        </div>
      ) : null}

      <div className="grid gap-5 md:grid-cols-3">
        {data?.plans.map((plan) => {
          const isPaid = plan.tier !== 'free';
          const featured = plan.tier === 'standard';
          const price = plan.priceUsdCents[active] ?? 0;
          const perMonth = plan.monthlyEquivalentCents[active] ?? 0;
          return (
            <div
              key={plan.tier}
              className={`card flex flex-col p-4 sm:p-6 ${featured ? 'order-first ring-2 ring-brand-600 md:order-none' : ''}`}
            >
              {featured ? (
                <span className="badge mb-3 self-start bg-brand-50 dark:bg-brand-500/15 text-brand-800 dark:text-brand-300">Most popular</span>
              ) : null}
              <h2 className="text-lg font-bold text-ink">{plan.label}</h2>
              <p className="mt-2">
                <span className="text-3xl font-bold text-ink">${(price / 100).toFixed(0)}</span>
                <span className="text-sm text-muted"> {isPaid ? INTERVAL_SUFFIX[active] : 'forever'}</span>
              </p>
              {isPaid && active !== 'month' ? (
                <p className="mt-1 text-xs text-muted">
                  Works out at ${(perMonth / 100).toFixed(2)} a month.
                </p>
              ) : null}

              <ul className="mt-5 flex-1 space-y-2.5">
                {plan.features.map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-muted">
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 0 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
                    </svg>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                {!isPaid ? (
                  <Link to="/register" className="btn-secondary w-full">Start free</Link>
                ) : !me?.user ? (
                  <Link to="/register" className="btn-primary w-full">Create an account</Link>
                ) : (
                  <button
                    type="button"
                    className={featured ? 'btn-primary w-full' : 'btn-secondary w-full'}
                    disabled={!data.stripeConfigured || busy !== null}
                    onClick={() => void checkout(plan.tier as 'standard' | 'advanced')}
                  >
                    {busy === plan.tier ? 'Redirecting…' : `Choose ${plan.label}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-sm text-muted">
        {active === 'quarter'
          ? 'The 3-month bundle renews quarterly and can be cancelled any time from your account settings. '
          : 'Cancel any time from your account settings. '}
        See our{' '}
        <Link to="/refunds" className="underline underline-offset-2">refund and cancellation policy</Link>.
      </p>
    </div>
  );
}
